import React, { FC, useState, useEffect, useCallback, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import Search from '../../components/Search'
import Pagination from '../../components/Pagination'
import ToggleButtonGroup from '../../components/ToggleButtonGroup'
import useTitle from 'react-use/lib/useTitle'
import Splash from '../../components/Splash'
import OrdersPlaceholder from '../../manager/orders/OrdersPlaceholder'
import CycleCountRow from './CycleCountRow'
import { IAccount, IBranch, IUser } from '../../types/user'
import {
  LocationTypes,
  LOCATION_TYPE_PLURAL_LABELS,
} from '../../types/location'
import withAccountAndBranch from '../../common/withAccountAndBranch'
import useDebounce from 'react-use/lib/useDebounce'
import { useSelector } from 'react-redux'
import useThunkDispatch from '../../common/useThunkDispatch'
import { fetchCycleCounts } from '../cycleCountsActions'
import {
  cycleCountsSelector,
  cycleCountsSearchIndexSelector,
} from '../cycleCountsSelectors'
import { ICycleCount } from '../../types/cycle-counts'
import { getShouldRenderTruckInventoryFeature } from '../../common/getShouldRenderTruckInventoryFeature'
import { getShouldRenderBinInventoryFeature } from '../../common/getShouldRenderBinInventoryFeature'

const PAGE_SIZE = 10

type LocationTypeFilter = 'all' | 'truck' | 'bin'
type StatusFilter = 'attention' | 'completed' | 'all'

interface ICycleCountsProps {
  branch?: IBranch
  user: IUser
  account?: IAccount
}

export const TABLE_WIDTHS = {
  STATUS: '-g8',
  TYPE: '-fix100',
  LOCATION_CODE: '-w12',
  COUNTED_DATE: '-fix150',
  COUNTED_BY: '-g16',
  APPROVED_BY: '-g16',
  LOCATION_AMOUNT: '-fix150',
  COUNTED_AMOUNT: '-fix150',
  VARIANCE: '-fix150',
  TOTAL_VARIANCE: '-g20',
}

const NEED_ATTENTION_STATUSES = new Set(['pending', 'rejected'])

const CycleCounts: FC<ICycleCountsProps> = ({ branch, user, account }) => {
  useTitle('Cycle Counts - Forshaw')

  const navigate = useNavigate()
  const routerLocation = useLocation()

  const [loading, setLoading] = useState<boolean>(false)
  const routerState = routerLocation.state as any
  const [typeFilter, setTypeFilter] = useState<LocationTypeFilter>(
    routerState?.typeFilter || 'all',
  )
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(
    routerState?.statusFilter || 'attention',
  )
  const [term, setTerm] = useState(routerState?.term || '')
  const [currentPage, setCurrentPage] = useState(1)
  const [filteredCycleCounts, setFilteredCycleCounts] = useState<ICycleCount[]>(
    [],
  )

  const dispatch = useThunkDispatch()
  const cycleCounts = useSelector(cycleCountsSelector)
  const cycleCountsIndex = useSelector(cycleCountsSearchIndexSelector)

  const currentAccount = account ?? user.account
  const truckEnabled = getShouldRenderTruckInventoryFeature(
    currentAccount?.truckInventoryFeature,
    branch?.truckInventoryFeature,
  )
  const binEnabled = getShouldRenderBinInventoryFeature(
    currentAccount?.binInventoryFeature,
    branch?.binInventoryFeature,
  )
  const bothEnabled = truckEnabled && binEnabled

  const loadCycleCounts = useCallback(async () => {
    if (!user || !branch) return
    const accountId = account?.id ?? user.account?.id
    const branchId = branch.id
    if (accountId === undefined) return
    setLoading(true)
    try {
      await dispatch(fetchCycleCounts(accountId, branchId))
    } catch (e) {
      console.error('Error loading cycle counts:', e)
    }
    setLoading(false)
  }, [user, branch, account, dispatch])

  useEffect(() => {
    if (branch) {
      loadCycleCounts()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branch, routerLocation.pathname])

  const featureFilteredCycleCounts = useMemo(() => {
    if (truckEnabled && binEnabled) return cycleCounts
    if (truckEnabled)
      return cycleCounts.filter(
        (cc) => cc.location?.locationType === LocationTypes.TRUCK,
      )
    if (binEnabled)
      return cycleCounts.filter(
        (cc) => cc.location?.locationType === LocationTypes.BIN,
      )
    return []
  }, [cycleCounts, truckEnabled, binEnabled])

  const typeFilteredCycleCounts = useMemo(() => {
    if (!bothEnabled || typeFilter === 'all') return featureFilteredCycleCounts
    return featureFilteredCycleCounts.filter(
      (cc) => cc.location?.locationType === typeFilter,
    )
  }, [featureFilteredCycleCounts, typeFilter, bothEnabled])

  const needAttentionCount = useMemo(
    () =>
      typeFilteredCycleCounts.filter((cc) =>
        NEED_ATTENTION_STATUSES.has(cc.status),
      ).length,
    [typeFilteredCycleCounts],
  )

  const statusFilteredCycleCounts = useMemo(() => {
    if (statusFilter === 'all') return typeFilteredCycleCounts
    if (statusFilter === 'completed')
      return typeFilteredCycleCounts.filter((cc) => cc.status === 'completed')
    return typeFilteredCycleCounts.filter((cc) =>
      NEED_ATTENTION_STATUSES.has(cc.status),
    )
  }, [typeFilteredCycleCounts, statusFilter])

  const sortedBase = useMemo(() => {
    const uniqueByIdMap = new Map<number, ICycleCount>()
    for (const cc of statusFilteredCycleCounts) {
      if (!uniqueByIdMap.has(cc.id)) uniqueByIdMap.set(cc.id, cc)
    }
    const items = Array.from(uniqueByIdMap.values())
    const statusPriority = { pending: 0, rejected: 1, completed: 2 } as const
    const getOrder = (s: string) =>
      (statusPriority as Record<string, number>)[s] ?? 3
    return items.sort((a, b) => {
      const diff = getOrder(a.status) - getOrder(b.status)
      if (diff !== 0) return diff
      const aTime = a.countedAt ? new Date(a.countedAt).getTime() : 0
      const bTime = b.countedAt ? new Date(b.countedAt).getTime() : 0
      return bTime - aTime
    })
  }, [statusFilteredCycleCounts])

  useDebounce(
    () => {
      setCurrentPage(1)
      if (term && cycleCountsIndex) {
        const searchResults = cycleCountsIndex.search(term)
        const searchIds = new Set(searchResults.map((cc) => cc.id))
        setFilteredCycleCounts(sortedBase.filter((cc) => searchIds.has(cc.id)))
      } else {
        setFilteredCycleCounts(sortedBase)
      }
    },
    300,
    [term, cycleCountsIndex, sortedBase],
  )

  useEffect(() => {
    setCurrentPage(1)
    if (term && cycleCountsIndex) {
      const searchIds = new Set(
        cycleCountsIndex.search(term).map((cc) => cc.id),
      )
      setFilteredCycleCounts(sortedBase.filter((cc) => searchIds.has(cc.id)))
    } else {
      setFilteredCycleCounts(sortedBase)
    }
  }, [sortedBase])

  const pageItems = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE
    return filteredCycleCounts.slice(start, start + PAGE_SIZE)
  }, [filteredCycleCounts, currentPage])

  const nothingFound = !filteredCycleCounts.length && !loading
  const showTypeColumn = !!bothEnabled

  const navigateTo = (cycleCount: ICycleCount) => {
    const base =
      account && branch?.id
        ? `/clients/${account.id}/branches/${branch.id}`
        : ''
    navigate(
      base
        ? `${base}/cycle_counts/${cycleCount.id}`
        : `/cycle_counts/${cycleCount.id}`,
      { state: { typeFilter, statusFilter, term } },
    )
  }

  return (
    <Splash isLoading={loading} withWrapper className="container">
      <div className="filter">
        {bothEnabled && (
          <>
            <div className="filter_col">
              <ToggleButtonGroup<LocationTypeFilter>
                options={['all', 'bin', 'truck']}
                value={typeFilter}
                renderOption={(t) => LOCATION_TYPE_PLURAL_LABELS[t] ?? 'All'}
                onChange={(t) => {
                  setTypeFilter(t)
                  setCurrentPage(1)
                }}
              />
            </div>
            <div className="filter_sep" />
          </>
        )}
        <div className="filter_col">
          <ToggleButtonGroup<StatusFilter>
            options={['attention', 'completed', 'all']}
            value={statusFilter}
            renderOption={(s) => {
              if (s === 'attention')
                return needAttentionCount > 0 ? (
                  <>Need&nbsp;attention&nbsp;({needAttentionCount})</>
                ) : (
                  <>Need&nbsp;attention</>
                )
              if (s === 'completed') return 'Completed'
              return 'All'
            }}
            onChange={(s) => {
              setStatusFilter(s)
              setCurrentPage(1)
            }}
          />
        </div>
        <div className="filter_sep" />
        <div className="filter_col -large">
          <Search
            searching={loading}
            placeholder="Search by code or user name"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
          />
        </div>
      </div>

      {!nothingFound && (
        <div className="table">
          <div className="table_head-tr -fixed-height">
            <div className={`table_td table_title ${TABLE_WIDTHS.STATUS}`}>
              Status
            </div>
            {showTypeColumn && (
              <div className={`table_td table_title ${TABLE_WIDTHS.TYPE}`}>
                Type
              </div>
            )}
            <div
              className={`table_td table_title ${TABLE_WIDTHS.LOCATION_CODE}`}
            >
              Location Code
            </div>
            <div
              className={`table_td table_title ${TABLE_WIDTHS.COUNTED_DATE}`}
            >
              Counted Date
            </div>
            <div className={`table_td table_title ${TABLE_WIDTHS.COUNTED_BY}`}>
              Counted By
            </div>
            <div className={`table_td table_title ${TABLE_WIDTHS.APPROVED_BY}`}>
              Approved By
            </div>
            <div
              className={`table_td table_title ${TABLE_WIDTHS.LOCATION_AMOUNT}`}
            >
              Location Amount
            </div>
            <div
              className={`table_td table_title ${TABLE_WIDTHS.COUNTED_AMOUNT}`}
            >
              Counted Amount
            </div>
            <div className={`table_td table_title ${TABLE_WIDTHS.VARIANCE}`}>
              Variance
            </div>
            <div
              className={`table_td table_title ${TABLE_WIDTHS.TOTAL_VARIANCE}`}
            >
              Total Variance
            </div>
          </div>
          {pageItems.map((cc) => (
            <CycleCountRow
              key={cc.id}
              cycleCount={cc}
              showTypeColumn={showTypeColumn}
              onClick={navigateTo}
            />
          ))}
        </div>
      )}

      {nothingFound && (
        <OrdersPlaceholder
          message="No cycle counts found"
          style={{ height: 'calc(100vh - 300px)' }}
        />
      )}

      <Pagination
        currentPage={currentPage}
        totalItems={filteredCycleCounts.length}
        pageSize={PAGE_SIZE}
        onPageChange={setCurrentPage}
      />

      <Splash isLoading={loading} bgColor="#F2F6FA" />
    </Splash>
  )
}

export default withAccountAndBranch(CycleCounts)
